import React, { useEffect, useRef } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { Text } from '../../ui/typography';
import { Card, CardHeader, CardContent } from '../../ui/card';

interface Props {
  title: string;
  children: React.ReactNode;
  onHeightChange?: (height: number) => void;
  footer?: React.ReactNode;
  allowResize?: boolean;
  onRemove?: () => void;
  minHeight?: number;
}

const useStyles = makeStyles<Theme, { minHeight?: number }>((theme: Theme) => ({
  paper: {
    background: 'transparent',
    boxShadow: 'none',
    overflow: 'hidden',
    minHeight: ({ minHeight }) => (minHeight ? `${minHeight}px` : 'none'),
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  resizable: {
    resize: 'vertical',
  },
  details: {
    paddingTop: '0 !important',
  },
  summaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
  },
  footer: {
    paddingTop: 16,
  },
}));

export const ChartContainer: React.FC<Props> = ({
  title,
  children,
  onHeightChange,
  footer,
  allowResize,
  onRemove,
  minHeight,
}) => {
  const classes = useStyles({ minHeight });
  const containerRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const currentHeightRef = useRef<number>(0);

  const mouseDownHandler = () => {
    if (!containerRef.current) return;
    resizeObserverRef.current = new ResizeObserver((entries) => {
      const containerHeight = entries[0].contentRect.height;
      currentHeightRef.current = containerHeight;
    });

    resizeObserverRef.current.observe(containerRef.current);
  };

  const mouseUpHandler = () => {
    if (containerRef.current && resizeObserverRef.current) {
      resizeObserverRef.current.unobserve(containerRef.current);
      resizeObserverRef.current = null;

      if (onHeightChange && currentHeightRef.current > 0) {
        const summaryHeight = summaryRef.current ? summaryRef.current.clientHeight : 0;
        const footerHeight = footerRef.current ? footerRef.current.clientHeight : 0;

        const height = currentHeightRef.current - summaryHeight - footerHeight - 16;

        onHeightChange(height);
      }
    }
  };

  useEffect(() => {
    const currentContainer = containerRef.current;

    return () => {
      if (currentContainer && resizeObserverRef.current) {
        resizeObserverRef.current.unobserve(currentContainer);
      }
    };
  }, []);

  return (
    <Card
      className={`${classes.paper} ${allowResize ? classes.resizable : ''}`}
      ref={containerRef}
      onMouseDown={mouseDownHandler}
      onMouseUp={mouseUpHandler}
    >
      <div ref={summaryRef}>
        <CardHeader onClick={onRemove}>
          <Text fontSize={12} fontFamily="mono" className={classes.summaryText} title={title}>
            {title}
          </Text>
        </CardHeader>
      </div>
      <CardContent className={classes.details}>
        {children}
        {footer && (
          <div className={classes.footer} ref={footerRef}>
            {footer}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChartContainer;
